import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    ChevronLeft, ChevronRight, TreePine, Mountain, Waves,
    Heart, Camera, Tent, Users, Check,
    X, MapPin, Compass, Lightbulb, Plus, ArrowRight, CalendarDays, Star,
    UtensilsCrossed, Sparkles, Home, Palette, Accessibility, Send, CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const interestDefs = [
    { id: 'safari', label: 'Safari', icon: TreePine, desc: 'Game drives & the Big Five' },
    { id: 'mountain', label: 'Mountains', icon: Mountain, desc: 'Kilimanjaro & highland treks' },
    { id: 'beach', label: 'Beach & Islands', icon: Waves, desc: 'Zanzibar, Pemba & coastal escapes' },
    { id: 'cultural', label: 'Cultural', icon: Heart, desc: 'Maasai villages & historic towns' },
    { id: 'wildlife', label: 'Photography', icon: Camera, desc: 'Capture stunning wildlife shots' },
    { id: 'adventure', label: 'Adventure', icon: Tent, desc: 'Camping, diving & off-road trails' }
];

const budgetRanges = [
    { id: 'budget', label: 'Budget-friendly', range: '$500 – $1,500', min: 500, max: 1500, emoji: '🎒' },
    { id: 'mid', label: 'Mid-range', range: '$1,500 – $3,500', min: 1500, max: 3500, emoji: '🏡' },
    { id: 'luxury', label: 'Luxury', range: '$3,500 – $7,000', min: 3500, max: 7000, emoji: '🏨' },
    { id: 'ultra', label: 'Ultra luxury', range: '$7,000+', min: 7000, max: 20000, emoji: '🏰' }
];

const groupOptions = [
    { id: 'solo', label: 'Solo', count: 1 },
    { id: 'couple', label: 'Couple', count: 2 },
    { id: 'small', label: 'Small group', count: 4 },
    { id: 'family', label: 'Family', count: 6 },
    { id: 'large', label: 'Large group', count: 10 }
];

const preferenceGroups = [
    { label: 'Travel', desc: 'Who\'s traveling', icon: Users, items: ['Family with kids', 'Traveling with infants', 'Seniors / slow-paced travel', 'Group trip'] },
    { label: 'Diet', desc: 'Food & dietary', icon: UtensilsCrossed, items: ['Vegetarian', 'Vegan-friendly', 'Halal food', 'Gluten-free'] },
    { label: 'Access', desc: 'Mobility needs', icon: Accessibility, items: ['Wheelchair accessible', 'Limited mobility support'] },
    { label: 'Moments', desc: 'Special occasions', icon: Sparkles, items: ['Honeymoon', 'Anniversary', 'Birthday celebration', 'First time in Tanzania'] },
    { label: 'Comfort', desc: 'Stay preferences', icon: Home, items: ['Luxury stays only', 'Eco-friendly lodges', 'Local/authentic experiences', 'Private tour only'] },
    { label: 'Interests', desc: 'Activity focus', icon: Palette, items: ['Photography', 'Wildlife focus', 'Cultural immersion', 'Adventure activities'] }
];

export default function TripWizard() {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, api, user } = useAuth();
    const navigate = useNavigate();

    const [selectedInterests, setSelectedInterests] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState({});
    const [openToSuggestions, setOpenToSuggestions] = useState({});
    const [dateRange, setDateRange] = useState({ from: null, to: null });
    const [selectedBudget, setSelectedBudget] = useState(null);
    const [selectedGroupSize, setSelectedGroupSize] = useState(null);
    const [selectedPreferences, setSelectedPreferences] = useState([]);
    const [specialRequests, setSpecialRequests] = useState('');
    const [prefTab, setPrefTab] = useState(0);
    const [suggestionPopup, setSuggestionPopup] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const suggestedPrefs = [];
    if (selectedGroupSize === 'family') suggestedPrefs.push('Family with kids');
    if (selectedGroupSize === 'couple') suggestedPrefs.push('Honeymoon');

    // Show popup when entering Step 6 with suggestions
    useEffect(() => {
        if (step === 6 && suggestedPrefs.length > 0) {
            const unselected = suggestedPrefs.filter(p => !selectedPreferences.includes(p));
            if (unselected.length > 0) {
                setSuggestionPopup({
                    prefs: unselected,
                    message: selectedGroupSize === 'family'
                        ? 'Traveling with family? We suggest adding:'
                        : 'Traveling as a couple? Consider adding:'
                });
            }
        } else {
            setSuggestionPopup(null);
        }
    }, [step]);

    const acceptSuggestion = (pref) => {
        setSelectedPreferences(p => p.includes(pref) ? p : [...p, pref]);
        setSuggestionPopup(prev => {
            if (!prev) return null;
            const remaining = prev.prefs.filter(p => p !== pref);
            return remaining.length > 0 ? { ...prev, prefs: remaining } : null;
        });
    };

    const togglePreference = (pref) => setSelectedPreferences(p => p.includes(pref) ? p.filter(i => i !== pref) : [...p, pref]);

    const totalSteps = 7;

    useEffect(() => {
        axios.get(`${API_URL}/api/destinations`).then(r => setDestinations(r.data)).catch(() => {});
    }, []);

    const toggleInterest = (id) => setSelectedInterests(p => p.includes(id) ? p.filter(i => i !== id) : [...p, id]);

    const toggleDestination = (interestId, destId) => {
        setSelectedDestinations(prev => {
            const current = prev[interestId] || [];
            const updated = current.includes(destId) ? current.filter(d => d !== destId) : [...current, destId];
            return { ...prev, [interestId]: updated };
        });
        if (openToSuggestions[interestId]) {
            setOpenToSuggestions(prev => ({ ...prev, [interestId]: false }));
        }
    };

    const toggleOpenToSuggestions = (interestId) => {
        setOpenToSuggestions(prev => ({ ...prev, [interestId]: !prev[interestId] }));
        if (!openToSuggestions[interestId]) {
            setSelectedDestinations(prev => ({ ...prev, [interestId]: [] }));
        }
    };

    const canProceed = () => {
        switch (step) {
            case 1: return selectedInterests.length > 0;
            case 2: return selectedInterests.every(id => 
                (selectedDestinations[id]?.length > 0) || openToSuggestions[id]
            );
            case 3: return dateRange.from && dateRange.to;
            case 4: return selectedBudget !== null;
            case 5: return selectedGroupSize !== null;
            case 6: return true;
            case 7: return true;
            default: return false;
        }
    };

    const dayCount = dateRange.from && dateRange.to
        ? Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)) + 1
        : 0;

    const getDestinationsForInterest = (interestId) => {
        return destinations.filter(d => d.category === interestId);
    };

    // Save wizard state to sessionStorage before redirecting to auth
    const saveWizardSnapshot = () => {
        const snapshot = {
            step, selectedInterests, selectedDestinations, openToSuggestions,
            dateRange: { from: dateRange.from?.toISOString() || null, to: dateRange.to?.toISOString() || null },
            selectedBudget, selectedGroupSize, selectedPreferences, specialRequests, prefTab
        };
        sessionStorage.setItem('tripWizardSnapshot', JSON.stringify(snapshot));
    };

    // Restore wizard state from sessionStorage on mount
    useEffect(() => {
        const saved = sessionStorage.getItem('tripWizardSnapshot');
        if (saved) {
            try {
                const s = JSON.parse(saved);
                if (s.selectedInterests) setSelectedInterests(s.selectedInterests);
                if (s.selectedDestinations) setSelectedDestinations(s.selectedDestinations);
                if (s.openToSuggestions) setOpenToSuggestions(s.openToSuggestions);
                if (s.dateRange?.from) setDateRange({ from: new Date(s.dateRange.from), to: s.dateRange.to ? new Date(s.dateRange.to) : null });
                if (s.selectedBudget) setSelectedBudget(s.selectedBudget);
                if (s.selectedGroupSize) setSelectedGroupSize(s.selectedGroupSize);
                if (s.selectedPreferences) setSelectedPreferences(s.selectedPreferences);
                if (s.specialRequests) setSpecialRequests(s.specialRequests);
                if (s.prefTab !== undefined) setPrefTab(s.prefTab);
                if (s.step) setStep(s.step);
                sessionStorage.removeItem('tripWizardSnapshot');
            } catch {}
        }
    }, []);

    const handleSubmit = async () => {
        if (!isAuthenticated) {
            saveWizardSnapshot();
            toast.info('Please sign in first to submit your trip');
            navigate('/auth');
            return;
        }
        if (user?.role !== 'traveler') { toast.error('Only travelers can create trips'); return; }
        setLoading(true);

        const destNames = [];
        selectedInterests.forEach(id => {
            if (openToSuggestions[id]) {
                destNames.push(`${interestDefs.find(i => i.id === id)?.label || id}: Open to suggestions`);
            } else {
                (selectedDestinations[id] || []).forEach(dId => {
                    const dest = destinations.find(d => d.id === dId);
                    if (dest) destNames.push(dest.name);
                });
            }
        });

        try {
            await api.post('/trips', {
                destination_interests: selectedInterests,
                specific_destinations: destNames,
                open_to_suggestions: Object.entries(openToSuggestions).filter(([k, v]) => v && selectedInterests.includes(k)).map(([k]) => k),
                travel_dates: { start: dateRange.from.toISOString(), end: dateRange.to.toISOString() },
                budget_range: { min: budgetRanges.find(b => b.id === selectedBudget)?.min || 0, max: budgetRanges.find(b => b.id === selectedBudget)?.max || 0, currency: 'USD' },
                group_size: groupOptions.find(g => g.id === selectedGroupSize)?.count || 1,
                preferences: selectedPreferences,
                special_requests: specialRequests || null,
                broadcast_summary: generateBroadcastSummary()
            });
            sessionStorage.removeItem('tripWizardSnapshot');
            setSubmitted(true);
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create trip'); }
        finally { setLoading(false); }
    };

    const getDestinationNames = () => {
        const names = [];
        selectedInterests.forEach(id => {
            (selectedDestinations[id] || []).forEach(dId => {
                const dest = destinations.find(d => d.id === dId);
                if (dest) names.push(dest.name);
            });
        });
        return names;
    };

    const generateBroadcastSummary = () => {
        const budget = budgetRanges.find(b => b.id === selectedBudget);
        const group = groupOptions.find(g => g.id === selectedGroupSize);
        const summaryNames = { safari: 'safari', mountain: 'mountain', beach: 'beach', cultural: 'cultural', wildlife: 'photography', adventure: 'adventure' };
        const interestLabels = selectedInterests.map(id => summaryNames[id] || id);
        const destNames = getDestinationNames();
        const monthName = dateRange.from ? format(dateRange.from, 'MMMM') : '';

        const parts = [];
        if (dayCount > 0) parts.push(`${dayCount}-day`);
        if (budget) parts.push(budget.label.toLowerCase());
        if (interestLabels.length === 1) {
            parts.push(`${interestLabels[0]} trip`);
        } else if (interestLabels.length === 2) {
            parts.push(`${interestLabels[0]} & ${interestLabels[1]} trip`);
        } else if (interestLabels.length > 2) {
            const last = interestLabels[interestLabels.length - 1];
            parts.push(`${interestLabels.slice(0, -1).join(', ')} & ${last} trip`);
        }
        if (group) {
            parts.push(`for ${group.count} ${group.count === 1 ? 'person' : 'people'}`);
        }
        if (monthName) parts.push(`in ${monthName}`);

        let summary = parts.join(' ');

        if (destNames.length === 1) {
            summary += `, visiting ${destNames[0]}`;
        } else if (destNames.length === 2) {
            summary += `, combining ${destNames[0]} and ${destNames[1]}`;
        } else if (destNames.length > 2) {
            const lastDest = destNames[destNames.length - 1];
            summary += `, combining ${destNames.slice(0, -1).join(', ')} and ${lastDest}`;
        }

        if (budget) {
            const fmtMin = budget.min.toLocaleString();
            const fmtMax = budget.max.toLocaleString();
            summary += `, budget $${fmtMin}–$${fmtMax} per person`;
        }

        return summary.charAt(0).toUpperCase() + summary.slice(1) + '.';
    };

    const stepHeaders = [
        { title: 'What kind of trip are you dreaming of?', sub: 'Choose everything that sparks your interest' },
        { title: 'Where exactly do you want to go?', sub: 'Pick specific destinations or let operators surprise you' },
        { title: 'When do you want to go?', sub: 'Pick your travel dates' },
        { title: "What's your budget?", sub: 'Per person, excluding international flights' },
        { title: 'Who\'s coming along?', sub: 'Tell us about your group' },
        { title: 'Anything else we should know?', sub: 'Help operators tailor the perfect trip for you' },
        { title: 'Your trip at a glance', sub: 'This is what operators will see — make sure it looks right' }
    ];

    const stepLabels = ['Interests', 'Destinations', 'Dates', 'Budget', 'Group', 'Details', 'Review'];

    const getSuggestion = () => {
        switch (step) {
            case 1:
                if (selectedInterests.length === 0) return 'Most travelers pick 2–3 interests for a well-rounded trip.';
                if (selectedInterests.length === 1) {
                    if (selectedInterests.includes('safari')) return 'Safari pairs beautifully with Beach & Islands — consider adding it!';
                    if (selectedInterests.includes('mountain')) return 'Mountain treks go great with Cultural experiences nearby.';
                    if (selectedInterests.includes('beach')) return 'Beach lovers often add a Safari day trip — best of both worlds!';
                    return 'Adding one more interest gives operators more flexibility to craft your perfect trip.';
                }
                if (selectedInterests.length >= 4) return 'Great variety! Keep in mind more interests may need a longer trip.';
                if (selectedInterests.includes('safari') && selectedInterests.includes('beach'))
                    return 'Classic combo! Safari + Beach is Tanzania\'s most popular itinerary.';
                return 'Nice picks! Operators love crafting multi-interest itineraries.';
            case 2:
                const allOpen = selectedInterests.every(id => openToSuggestions[id]);
                if (allOpen) return 'Leaving it to the experts — operators will suggest their hidden gems!';
                const totalDests = Object.values(selectedDestinations).flat().length;
                if (totalDests === 0) return 'Pick specific spots you\'ve been dreaming about, or let operators surprise you.';
                if (totalDests >= 5) return 'That\'s quite a list! A longer trip (10+ days) would do these justice.';
                return 'Mixing specific picks with "open to suggestions" gives operators creative freedom.';
            case 3:
                if (!dateRange.from) return 'The dry season (Jun–Oct) is ideal for safaris; Nov–Mar is best for beaches.';
                if (dayCount > 0 && dayCount < 4) return 'Short trips work best with 1–2 interests. Consider extending for a richer experience.';
                if (dayCount >= 4 && dayCount <= 7) return 'Perfect duration for most Tanzania trips — enough time without feeling rushed.';
                if (dayCount > 14) return 'An extended trip! You\'ll have time to explore Tanzania in depth.';
                if (dayCount > 7) return 'Great length! You can comfortably cover multiple regions.';
                return 'Pick dates that work for you — operators can adjust around availability.';
            case 4:
                if (!selectedBudget) return 'Budget includes accommodation, transport, guides & park fees — not international flights.';
                if (selectedBudget === 'budget') return 'Budget-friendly trips focus on camping & group safaris — still amazing experiences!';
                if (selectedBudget === 'mid') return 'Mid-range unlocks comfortable lodges and private game drives.';
                if (selectedBudget === 'luxury') return 'Luxury gets you top lodges, private vehicles & exclusive experiences.';
                if (selectedBudget === 'ultra') return 'Ultra luxury — fly-in safaris, private reserves & world-class service await.';
                return '';
            case 5:
                if (!selectedGroupSize) return 'Group size affects pricing — solo travelers pay more per person than groups.';
                if (selectedGroupSize === 'solo') return 'Solo travel in Tanzania is safe and popular — you\'ll get a personal guide!';
                if (selectedGroupSize === 'couple') return 'Romantic getaway? Ask about honeymoon packages in your special requests!';
                if (selectedGroupSize === 'family') return 'Family trips are wonderful — mention kids\' ages in special requests for tailored activities.';
                if (selectedGroupSize === 'large') return 'Large groups get better per-person rates. Operators can split into smaller vehicles.';
                return 'Small groups enjoy a good balance of privacy and value.';
            case 6:
                if (selectedPreferences.length === 0 && !specialRequests) return 'Preferences help operators tailor your trip — even one or two selections make a difference.';
                if (selectedPreferences.includes('Family with kids')) return 'Great choice! Mention ages so operators can plan kid-friendly activities.';
                if (selectedPreferences.includes('Photography')) return 'Photo-focused? Operators can arrange extended game drives at golden hour.';
                if (selectedPreferences.includes('Honeymoon')) return 'Congratulations! Operators love crafting romantic surprises for couples.';
                return 'Almost done! Add any final details to help operators craft your dream trip.';
            case 7:
                return 'Review your trip summary below. This is what operators will receive — edit any step if needed.';
            default:
                return '';
        }
    };

    return (
        <>
        {/* ===== SUCCESS / CONFIRMATION PAGE ===== */}
        {submitted ? (
            <div className="min-h-screen bg-white flex flex-col" data-testid="trip-success-page">
                <div className="h-1.5 bg-gradient-to-r from-[#073763] via-[#0a5299] to-[#073763]" />
                <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12">
                    <div className="max-w-lg w-full text-center">
                        <div className="relative mx-auto w-20 h-20 mb-8">
                            <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-30" />
                            <div className="relative w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-200">
                                <CheckCircle2 size={40} className="text-white" strokeWidth={2.5} />
                            </div>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 tracking-tight mb-3" data-testid="success-title">
                            Your snapshot is live!
                        </h1>
                        <p className="text-base sm:text-lg text-neutral-500 leading-relaxed mb-8 max-w-md mx-auto">
                            Your trip request has been broadcast to verified operators across Tanzania. They'll review your details and start crafting personalized itineraries for you.
                        </p>
                        <div className="bg-neutral-50 rounded-2xl p-6 sm:p-8 text-left mb-8 border border-neutral-100">
                            <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wider mb-5">What happens next</h3>
                            <div className="space-y-5">
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#073763] flex items-center justify-center text-white text-xs font-bold shrink-0">1</div>
                                    <div>
                                        <p className="font-semibold text-neutral-800 text-sm">Operators receive your snapshot</p>
                                        <p className="text-sm text-neutral-500 mt-0.5">Verified tour operators matching your interests will see your request and start preparing proposals.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#073763] flex items-center justify-center text-white text-xs font-bold shrink-0">2</div>
                                    <div>
                                        <p className="font-semibold text-neutral-800 text-sm">Review tailored itineraries</p>
                                        <p className="text-sm text-neutral-500 mt-0.5">You'll receive detailed proposals with day-by-day plans, pricing, and accommodation options to compare.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-[#073763] flex items-center justify-center text-white text-xs font-bold shrink-0">3</div>
                                    <div>
                                        <p className="font-semibold text-neutral-800 text-sm">Confirm your booking</p>
                                        <p className="text-sm text-neutral-500 mt-0.5">Chat with operators, ask questions, and confirm the itinerary that best suits your preferences.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            className="bg-[#073763] hover:bg-[#052a4a] text-white rounded-full px-10 py-3.5 text-sm font-semibold shadow-lg shadow-[#073763]/20 transition-all hover:shadow-xl hover:shadow-[#073763]/30"
                            data-testid="success-go-dashboard"
                        >
                            <span className="flex items-center gap-2">Go to Dashboard <ArrowRight size={16} /></span>
                        </Button>
                        <p className="text-xs text-neutral-400 mt-5">You'll be notified when operators respond with proposals.</p>
                    </div>
                </div>
            </div>
        ) : (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Top Bar */}
            <div className="sticky top-0 z-30 bg-white border-b border-neutral-100">
                <div className="h-[3px] bg-neutral-100">
                    <div className="h-full bg-[#073763] transition-all duration-500 ease-out" style={{ width: `${(step / totalSteps) * 100}%` }} />
                </div>
                <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
                    <button
                        onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
                        className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 transition-colors shrink-0"
                        data-testid="wizard-back-btn"
                    >
                        <ChevronLeft size={18} />
                        <span className="hidden sm:inline">{step > 1 ? 'Back' : 'Exit'}</span>
                    </button>

                    {/* Step Timeline — abbreviated on mobile */}
                    <div className="flex items-center gap-0 mx-2 flex-1 justify-center overflow-hidden" data-testid="step-timeline">
                        {stepLabels.map((label, i) => {
                            const stepNum = i + 1;
                            const completed = stepNum < step;
                            const current = stepNum === step;
                            return (
                                <div key={i} className="flex items-center">
                                    <button
                                        onClick={() => { if (completed) setStep(stepNum); }}
                                        className={`flex items-center gap-1 transition-all ${completed ? 'cursor-pointer' : 'cursor-default'}`}
                                        disabled={!completed}
                                    >
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-all duration-300 ${
                                            completed ? 'bg-[#073763] text-white' :
                                            current ? 'bg-neutral-900 text-white' :
                                            'bg-neutral-200 text-neutral-400'
                                        }`}>
                                            {completed ? <Check size={11} strokeWidth={3} /> : stepNum}
                                        </div>
                                        <span className={`hidden sm:inline text-[11px] font-medium whitespace-nowrap transition-colors ${
                                            completed ? 'text-[#073763]' :
                                            current ? 'text-neutral-900' :
                                            'text-neutral-300'
                                        }`}>{label}</span>
                                        {current && <span className="sm:hidden text-[11px] font-medium whitespace-nowrap text-neutral-900">{label}</span>}
                                    </button>
                                    {i < stepLabels.length - 1 && (
                                        <div className={`w-2 sm:w-3 lg:w-5 h-[2px] mx-0.5 rounded-full transition-all duration-300 ${
                                            completed ? 'bg-[#073763]' : 'bg-neutral-200'
                                        }`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-neutral-100 rounded-full transition shrink-0" data-testid="wizard-close">
                        <X size={18} className="text-neutral-500" />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
                <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 pt-8 sm:pt-10 pb-32 flex-1">
                    <div className="mb-8 sm:mb-10">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 tracking-tight leading-tight" data-testid="step-title">
                            {stepHeaders[step - 1].title}
                        </h1>
                        <p className="text-base text-neutral-500 mt-2">{stepHeaders[step - 1].sub}</p>
                        {getSuggestion() && step !== 6 && (
                            <div className="mt-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="suggestion-banner">
                                <Lightbulb size={18} className="text-amber-500 mt-0.5 shrink-0" />
                                <p className="text-sm text-amber-800" data-testid="suggestion-text">{getSuggestion()}</p>
                            </div>
                        )}
                    </div>

                    {/* Step 1: Interests */}
                    {step === 1 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {interestDefs.map(item => {
                                const active = selectedInterests.includes(item.id);
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => toggleInterest(item.id)}
                                        className={`relative rounded-2xl p-5 border text-left transition-all duration-200 ${
                                            active ? 'border-neutral-900 bg-neutral-50 shadow-md' : 'border-neutral-200/70 bg-white hover:border-neutral-300 hover:shadow-sm'
                                        }`}
                                        data-testid={`interest-${item.id}`}
                                    >
                                        {active && (
                                            <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-neutral-900 flex items-center justify-center">
                                                <Check size={14} className="text-white" strokeWidth={2.5} />
                                            </div>
                                        )}
                                        <item.icon size={28} className={`mb-3 ${active ? 'text-neutral-900' : 'text-neutral-400'}`} strokeWidth={1.5} />
                                        <p className="font-semibold text-neutral-900 text-sm">{item.label}</p>
                                        <p className="text-xs text-neutral-400 mt-0.5">{item.desc}</p>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Step 2: Specific Destinations */}
                    {step === 2 && (
                        <div className="space-y-10">
                            {selectedInterests.map(interestId => {
                                const interest = interestDefs.find(i => i.id === interestId);
                                const dests = getDestinationsForInterest(interestId);
                                const isOpen = openToSuggestions[interestId];
                                const InterestIcon = interest?.icon || Compass;

                                return (
                                    <div key={interestId} data-testid={`dest-section-${interestId}`}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center">
                                                <InterestIcon size={20} className="text-neutral-700" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-neutral-900">{interest?.label}</h3>
                                                <p className="text-xs text-neutral-400">{dests.length} destination{dests.length !== 1 ? 's' : ''} available</p>
                                            </div>
                                        </div>

                                        {/* "Open to suggestions" toggle */}
                                        <button
                                            onClick={() => toggleOpenToSuggestions(interestId)}
                                            className={`w-full flex items-center gap-4 p-4 rounded-2xl border mb-3 transition-all duration-200 text-left ${
                                                isOpen ? 'border-[#073763] bg-[#073763]/5' : 'border-neutral-200/70 hover:border-neutral-300'
                                            }`}
                                            data-testid={`open-suggestions-${interestId}`}
                                        >
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                                isOpen ? 'bg-[#073763]' : 'bg-neutral-100'
                                            }`}>
                                                <Compass size={18} className={isOpen ? 'text-white' : 'text-neutral-400'} />
                                            </div>
                                            <div className="flex-1">
                                                <p className={`font-medium text-sm ${isOpen ? 'text-[#073763]' : 'text-neutral-700'}`}>Open to operator suggestions</p>
                                                <p className="text-xs text-neutral-400">Let experts recommend the best spots for you</p>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                                isOpen ? 'border-[#073763] bg-[#073763]' : 'border-neutral-300'
                                            }`}>
                                                {isOpen && <Check size={14} className="text-white" strokeWidth={3} />}
                                            </div>
                                        </button>

                                        {/* Destination cards */}
                                        {dests.length > 0 && (
                                            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 transition-opacity ${isOpen ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                                                {dests.map(dest => {
                                                    const selected = (selectedDestinations[interestId] || []).includes(dest.id);
                                                    return (
                                                        <button
                                                            key={dest.id}
                                                            onClick={() => toggleDestination(interestId, dest.id)}
                                                            className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all duration-200 ${
                                                                selected ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200/70 hover:border-neutral-300'
                                                            }`}
                                                            data-testid={`dest-${dest.id}`}
                                                        >
                                                            {dest.image_url ? (
                                                                <img src={dest.image_url} alt={dest.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
                                                                    <MapPin size={18} className="text-neutral-400" />
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-neutral-900 text-sm truncate">{dest.name}</p>
                                                                <p className="text-xs text-neutral-400 line-clamp-1">{dest.description?.slice(0, 60)}</p>
                                                            </div>
                                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                                                                selected ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-300'
                                                            }`}>
                                                                {selected && <Check size={12} className="text-white" strokeWidth={3} />}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {dests.length === 0 && !isOpen && (
                                            <p className="text-sm text-neutral-400 text-center py-4 bg-neutral-50 rounded-xl">No destinations added for this category yet. Choose "Open to suggestions" above.</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Step 3: Dates */}
                    {step === 3 && (
                        <div>
                            <div className="flex justify-center">
                                <Calendar
                                    mode="range"
                                    selected={dateRange}
                                    onSelect={setDateRange}
                                    numberOfMonths={2}
                                    disabled={(date) => date < new Date()}
                                    className="rounded-2xl border border-neutral-200/70 bg-white p-5 shadow-sm"
                                    data-testid="date-calendar"
                                />
                            </div>
                            {dateRange.from && dateRange.to && (
                                <div className="mt-8 flex flex-col items-center gap-3">
                                    <div className="flex items-center gap-4">
                                        <div className="bg-neutral-50 rounded-2xl px-6 py-4 text-center">
                                            <p className="text-xs text-neutral-400 mb-1">Start date</p>
                                            <p className="font-semibold text-neutral-900">{format(dateRange.from, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <ChevronRight size={18} className="text-neutral-300" />
                                        <div className="bg-neutral-50 rounded-2xl px-6 py-4 text-center">
                                            <p className="text-xs text-neutral-400 mb-1">End date</p>
                                            <p className="font-semibold text-neutral-900">{format(dateRange.to, 'MMM dd, yyyy')}</p>
                                        </div>
                                        <div className="bg-neutral-900 text-white rounded-2xl px-5 py-4 text-center">
                                            <p className="text-xs text-neutral-400 mb-1">Duration</p>
                                            <p className="font-bold">{dayCount} days</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setDateRange({ from: null, to: null })}
                                        className="text-sm text-neutral-400 hover:text-neutral-900 transition-colors underline underline-offset-2"
                                        data-testid="clear-dates-btn"
                                    >
                                        Clear dates
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 4: Budget */}
                    {step === 4 && (
                        <div className="space-y-3">
                            {budgetRanges.map(b => {
                                const active = selectedBudget === b.id;
                                return (
                                    <button
                                        key={b.id}
                                        onClick={() => { setSelectedBudget(b.id); setTimeout(() => setStep(s => s + 1), 400); }}
                                        className={`w-full flex items-center gap-5 p-5 rounded-2xl border transition-all duration-200 text-left ${
                                            active ? 'border-neutral-900 bg-neutral-50 shadow-md' : 'border-neutral-200/70 bg-white hover:border-neutral-300 hover:shadow-sm'
                                        }`}
                                        data-testid={`budget-${b.id}`}
                                    >
                                        <span className="text-3xl">{b.emoji}</span>
                                        <div className="flex-1">
                                            <p className="font-semibold text-neutral-900">{b.label}</p>
                                            <p className="text-sm text-neutral-500">{b.range} per person</p>
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                            active ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-300'
                                        }`}>
                                            {active && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Step 5: Group Size */}
                    {step === 5 && (
                        <div className="space-y-3">
                            {groupOptions.map(g => {
                                const active = selectedGroupSize === g.id;
                                return (
                                    <button
                                        key={g.id}
                                        onClick={() => { setSelectedGroupSize(g.id); setTimeout(() => setStep(s => s + 1), 400); }}
                                        className={`w-full flex items-center gap-5 p-5 rounded-2xl border transition-all duration-200 text-left ${
                                            active ? 'border-neutral-900 bg-neutral-50 shadow-md' : 'border-neutral-200/70 bg-white hover:border-neutral-300 hover:shadow-sm'
                                        }`}
                                        data-testid={`group-${g.id}`}
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                                            active ? 'bg-neutral-900' : 'bg-neutral-100'
                                        }`}>
                                            <Users size={20} className={active ? 'text-white' : 'text-neutral-500'} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-neutral-900">{g.label}</p>
                                            <p className="text-sm text-neutral-500">{g.count} {g.count === 1 ? 'traveler' : 'travelers'}</p>
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                            active ? 'border-neutral-900 bg-neutral-900' : 'border-neutral-300'
                                        }`}>
                                            {active && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Step 6: Preferences */}
                    {step === 6 && (
                        <div>
                            {/* Smart Suggestion Popup — compact */}
                            {suggestionPopup && (
                                <div className="mb-5 animate-in slide-in-from-top-3 fade-in duration-300" data-testid="suggestion-popup">
                                    <div className="flex items-center gap-3 bg-[#073763]/8 border border-[#073763]/20 rounded-xl px-4 py-3">
                                        <Lightbulb size={16} className="text-[#073763] shrink-0" />
                                        <p className="text-sm text-neutral-700 flex-1">{suggestionPopup.message}</p>
                                        {suggestionPopup.prefs.map(pref => (
                                            <button key={pref} onClick={() => acceptSuggestion(pref)} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#073763] text-white hover:bg-[#6da46c] transition-all shrink-0" data-testid={`accept-suggestion-${pref.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                                                <Plus size={12} /> {pref}
                                            </button>
                                        ))}
                                        <button onClick={() => setSuggestionPopup(null)} className="p-1 hover:bg-neutral-100 rounded-full transition shrink-0" data-testid="dismiss-suggestion">
                                            <X size={14} className="text-neutral-400" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Selected chips */}
                            {selectedPreferences.length > 0 && (
                                <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="selected-prefs-strip">
                                    <span className="text-[11px] font-bold text-neutral-300 uppercase tracking-widest shrink-0">{selectedPreferences.length}</span>
                                    {selectedPreferences.map(pref => (
                                        <span key={pref} className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs bg-neutral-900 text-white font-medium">
                                            {pref}
                                            <button onClick={() => togglePreference(pref)} className="hover:text-neutral-300 transition p-0.5"><X size={10} /></button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Preference Group Cards — Horizontal Row */}
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6" data-testid="pref-cards">
                                {preferenceGroups.map((group, i) => {
                                    const isSelected = prefTab === i;
                                    const activeCount = group.items.filter(p => selectedPreferences.includes(p)).length;
                                    const GroupIcon = group.icon;
                                    return (
                                        <button
                                            key={group.label}
                                            onClick={() => setPrefTab(i)}
                                            className={`relative aspect-square rounded-2xl border flex flex-col items-center justify-center gap-1.5 p-3 transition-all duration-200 ${
                                                isSelected
                                                    ? 'border-neutral-900 bg-neutral-900 shadow-lg'
                                                    : activeCount > 0
                                                        ? 'border-[#073763] bg-[#073763]/5 hover:shadow-md'
                                                        : 'border-neutral-200/70 bg-white hover:border-neutral-300 hover:shadow-sm'
                                            }`}
                                            data-testid={`pref-card-${i}`}
                                        >
                                            {activeCount > 0 && !isSelected && (
                                                <span className="absolute top-2 right-2 w-5 h-5 rounded-full text-[10px] font-bold bg-[#073763] text-white flex items-center justify-center">{activeCount}</span>
                                            )}
                                            {activeCount > 0 && isSelected && (
                                                <span className="absolute top-2 right-2 w-5 h-5 rounded-full text-[10px] font-bold bg-white text-neutral-900 flex items-center justify-center">{activeCount}</span>
                                            )}
                                            <GroupIcon size={26} className={isSelected ? 'text-white' : activeCount > 0 ? 'text-[#073763]' : 'text-neutral-400'} strokeWidth={1.5} />
                                            <span className={`text-xs font-bold leading-tight text-center ${isSelected ? 'text-white' : 'text-neutral-800'}`}>{group.label}</span>
                                            <span className={`text-[10px] leading-tight text-center ${isSelected ? 'text-neutral-300' : 'text-neutral-400'}`}>{group.desc}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Chips for selected group */}
                            <div className="mb-6 bg-neutral-50 rounded-2xl p-5 border border-neutral-50" data-testid={`pref-chips-${prefTab}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-sm font-semibold text-neutral-900">{preferenceGroups[prefTab].label}</span>
                                    <span className="text-xs text-neutral-400">{preferenceGroups[prefTab].desc}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {preferenceGroups[prefTab].items.map(pref => {
                                        const active = selectedPreferences.includes(pref);
                                        const suggested = !active && suggestedPrefs.includes(pref);
                                        return (
                                            <button
                                                key={pref}
                                                onClick={() => togglePreference(pref)}
                                                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-150 ${
                                                    active
                                                        ? 'bg-neutral-900 text-white border-neutral-900'
                                                        : suggested
                                                            ? 'bg-[#073763]/10 text-[#073763] border-[#073763]/40 border-dashed hover:bg-[#073763]/20'
                                                            : 'bg-white text-neutral-600 border-neutral-200/70 hover:border-neutral-300'
                                                }`}
                                                data-testid={`pref-${pref.replace(/[\s/]+/g, '-').toLowerCase()}`}
                                            >
                                                {active && <Check size={12} className="inline mr-1.5 -mt-0.5" strokeWidth={3} />}
                                                {pref}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Special Requests */}
                            <div className="relative">
                                <Label className="text-sm font-semibold text-neutral-900 mb-1 block">Special requests</Label>
                                <p className="text-[11px] text-neutral-400 mb-2">Dietary needs, celebrations, bucket-list moments...</p>
                                <textarea
                                    placeholder="We're celebrating a honeymoon and would love a quiet, romantic beach stay after safari"
                                    value={specialRequests}
                                    onChange={(e) => setSpecialRequests(e.target.value)}
                                    maxLength={500}
                                    className="w-full min-h-[100px] p-3.5 rounded-xl border border-neutral-200/70 bg-neutral-50/50 text-sm text-neutral-900 placeholder:text-neutral-300 focus:outline-none focus:ring-0 focus:border-neutral-900 focus:bg-white resize-none transition-all"
                                    data-testid="special-requests"
                                />
                                <span className="absolute bottom-2.5 right-3 text-[10px] text-neutral-300">{specialRequests.length}/500</span>
                            </div>
                        </div>
                    )}

                    {/* Step 7: Review — Broadcast Summary */}
                    {step === 7 && (
                        <div data-testid="review-step">
                            <div className="bg-neutral-50 border border-neutral-100 rounded-2xl p-8 mb-8">
                                <p className="text-lg sm:text-xl font-semibold text-neutral-900 leading-relaxed" data-testid="broadcast-summary">
                                    {generateBroadcastSummary()}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                                    <div className="flex items-center gap-3">
                                        <Heart size={16} className="text-neutral-400" />
                                        <span className="text-sm text-neutral-500">Interests</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 justify-end">
                                        {selectedInterests.map(id => (
                                            <span key={id} className="text-xs bg-neutral-900 text-white px-2.5 py-1 rounded-full">
                                                {interestDefs.find(i => i.id === id)?.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                                    <div className="flex items-center gap-3">
                                        <MapPin size={16} className="text-neutral-400" />
                                        <span className="text-sm text-neutral-500">Destinations</span>
                                    </div>
                                    <p className="text-sm font-medium text-neutral-900 text-right max-w-[60%]">
                                        {getDestinationNames().length > 0
                                            ? getDestinationNames().join(', ')
                                            : 'Open to suggestions'}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                                    <div className="flex items-center gap-3">
                                        <CalendarDays size={16} className="text-neutral-400" />
                                        <span className="text-sm text-neutral-500">Dates</span>
                                    </div>
                                    <p className="text-sm font-medium text-neutral-900">
                                        {dateRange.from && dateRange.to
                                            ? `${format(dateRange.from, 'MMM dd')} – ${format(dateRange.to, 'MMM dd, yyyy')} (${dayCount}d)`
                                            : '—'}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                                    <div className="flex items-center gap-3">
                                        <span className="text-neutral-400 text-base">$</span>
                                        <span className="text-sm text-neutral-500">Budget</span>
                                    </div>
                                    <p className="text-sm font-medium text-neutral-900">
                                        {budgetRanges.find(b => b.id === selectedBudget)?.range || '—'} per person
                                    </p>
                                </div>

                                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                                    <div className="flex items-center gap-3">
                                        <Users size={16} className="text-neutral-400" />
                                        <span className="text-sm text-neutral-500">Group</span>
                                    </div>
                                    <p className="text-sm font-medium text-neutral-900">
                                        {groupOptions.find(g => g.id === selectedGroupSize)?.label || '—'} ({groupOptions.find(g => g.id === selectedGroupSize)?.count || 0} {groupOptions.find(g => g.id === selectedGroupSize)?.count === 1 ? 'person' : 'people'})
                                    </p>
                                </div>

                                {selectedPreferences.length > 0 && (
                                    <div className="flex items-start justify-between py-3 border-b border-neutral-100">
                                        <div className="flex items-center gap-3 shrink-0">
                                            <Check size={16} className="text-neutral-400" />
                                            <span className="text-sm text-neutral-500">Preferences</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 justify-end max-w-[60%]">
                                            {selectedPreferences.map(p => (
                                                <span key={p} className="text-xs bg-neutral-100 text-neutral-600 px-2.5 py-1 rounded-full">{p}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {specialRequests && (
                                    <div className="py-3">
                                        <p className="text-sm text-neutral-500 mb-1">Special requests</p>
                                        <p className="text-sm text-neutral-700 italic">"{specialRequests}"</p>
                                    </div>
                                )}
                            </div>

                            <p className="mt-8 text-center text-xs text-neutral-400">
                                Tap any step above to make changes before submitting.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-100 z-30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                    <button
                        onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
                        className="text-sm font-semibold text-neutral-900 underline underline-offset-2 hover:text-neutral-600 transition"
                        data-testid="wizard-back-underline"
                    >
                        {step > 1 ? 'Back' : 'Exit'}
                    </button>

                    <div className="hidden sm:flex items-center gap-2">
                        {selectedInterests.length > 0 && (
                            <span className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full">
                                {selectedInterests.length} interest{selectedInterests.length > 1 ? 's' : ''}
                            </span>
                        )}
                        {dateRange.from && dateRange.to && (
                            <span className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full">{dayCount}d</span>
                        )}
                        {selectedBudget && (
                            <span className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full">
                                {budgetRanges.find(b => b.id === selectedBudget)?.label}
                            </span>
                        )}
                        {selectedGroupSize && (
                            <span className="text-xs bg-neutral-100 text-neutral-600 px-3 py-1 rounded-full">
                                {groupOptions.find(g => g.id === selectedGroupSize)?.count}p
                            </span>
                        )}
                    </div>

                    {step < totalSteps ? (
                        <Button
                            onClick={() => setStep(step + 1)}
                            disabled={!canProceed()}
                            className="bg-neutral-900 text-white hover:bg-neutral-800 rounded-xl px-8 py-3 text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                            data-testid="wizard-next-btn"
                        >
                            Next <ChevronRight size={16} className="ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="bg-gradient-to-r from-[#073763] to-[#5a9a59] text-white hover:from-[#052a4a] hover:to-[#4d8a4d] rounded-xl px-8 py-3 text-sm font-semibold shadow-lg shadow-[#073763]/20 transition-all"
                            data-testid="wizard-submit-btn"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creating...</span>
                            ) : (
                                <span className="flex items-center gap-2"><MapPin size={16} /> Get Proposals</span>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
        )}
        </>
    );
}
